import { useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';
import { getLegalErrorCopy } from 'App/Services/LegalPages';

import LEGAL_PAGE_TYPES from '../helpers/legalPageTypes.json';
import parseLegalHtml from '../helpers/parseLegalHtml';
import parseWpPage from '../helpers/parseWpPage';

/**
 * Joins URL path segments into a root-absolute path, ignoring empty parts
 * and extra slashes (e.g. `'/dt'`, `'en'`, `'terms.html'` → `'/dt/en/terms.html'`).
 *
 * @param {...string} parts - Path segments to join.
 * @returns {string} Root-absolute URL path.
 */
const joinUrlPath = (...parts) => {
    const cleaned = parts
        .filter(Boolean)
        .map((p) => String(p).replace(/^\/+|\/+$/g, ''))
        .filter(Boolean);

    return `/${cleaned.join('/')}`;
};

/**
 * Fetches legal content as either local HTML or remote WP JSON.
 *
 * @async
 * @param {string} reqUrl - Fully qualified URL to fetch.
 * @param {Object} [options]
 * @param {boolean} [options.parseHtml=false] - If true, parse the response as HTML.
 * @param {boolean} [options.reportFailure=false] - If true, report non-ok / thrown errors.
 * @param {string} [options.type] - Document type used only for error context.
 * @param {Map<string, Promise<object>>} [options.cache] - Successful requests cache.
 * @returns {Promise<{title: string, content: string} | null>}
 */
const fetchFile = async (
    reqUrl,
    { parseHtml = false, reportFailure = false, type, cache } = {}
) => {
    const cacheKey = `${parseHtml ? 'html' : 'json'}:${reqUrl}`;

    if (cache?.has(cacheKey)) {
        return cache.get(cacheKey);
    }

    const request = (async () => {
        try {
            const res = await fetch(reqUrl);

            if (res.ok) {
                if (parseHtml) {
                    return parseLegalHtml(await res.text());
                }

                const data = await res.json();

                return parseWpPage(data);
            }

            if (reportFailure) {
                Sentry.captureException(
                    TypeError(
                        `Failed to fetch ${parseHtml ? 'local' : 'remote'} file for Terms&Use modal with type - ${type}`
                    )
                );
            }

            return null;
        } catch (error) {
            if (reportFailure) {
                captureCustomException(error, 'LegalsContentLoadFailed');
            }

            return null;
        }
    })();

    cache?.set(cacheKey, request);

    const result = await request;

    if (!result && cache?.get(cacheKey) === request) {
        cache?.delete(cacheKey);
    }

    return result;
};

/**
 * Loads modal text from a local HTML file when `fetchLocal` is true, then
 * falls back to the remote CMS URL. Returns localized error copy if both fail.
 *
 * @async
 * @param {Object} params
 * @param {'privacy'|'about'|'cookies'|'terms'} params.type
 * @param {string} params.localUrl
 * @param {string} params.remoteUrl
 * @param {boolean} params.fetchLocal
 * @param {string} params.lang
 * @param {Map<string, Promise<object>>} params.cache
 * @returns {Promise<{title: string, content: string}>}
 */
const getContent = async ({
    type,
    localUrl,
    remoteUrl,
    fetchLocal,
    lang,
    cache,
}) => {
    if (fetchLocal && localUrl) {
        const local = await fetchFile(localUrl, {
            parseHtml: true,
            type,
            cache,
        });

        if (local) return local;
    }

    const remote = remoteUrl
        ? await fetchFile(remoteUrl, {
              reportFailure: true,
              type,
              cache,
          })
        : null;

    if (remote) return remote;

    return getLegalErrorCopy(lang);
};

/**
 * Builds root-absolute hrefs for cookies/terms/privacy from locale settings.
 *
 * @param {{ role?: string, [key: string]: { fileName?: string } }} localizedSetting
 * @param {string} language
 * @param {string} path - Base path without a trailing locale segment (e.g. `''`, `'/dt'`).
 * @returns {Record<'cookies'|'terms'|'privacy', string|null>}
 */
const getLocalLegalUrls = (localizedSetting, language, path) => {
    const prefixParts = [path];

    if (localizedSetting?.role !== 'main') {
        prefixParts.push(language);
    }

    const result = {};

    for (const type of LEGAL_PAGE_TYPES) {
        const fileName = localizedSetting?.[type]?.fileName;
        result[type] = fileName ? joinUrlPath(...prefixParts, fileName) : null;
    }

    return result;
};

/**
 * Manages Terms / Privacy / Cookies modal state and content retrieval.
 *
 * @param {Object} options
 * @param {string} options.path - Base path used to construct local asset URLs.
 * @param {string} options.lang - Current app language.
 * @param {Object} options.localeSettings - Per-locale map from `localization/settings.json`.
 * @param {boolean} [options.fetchLocal=false] - If true, try the clicked local HTML file before the remote `link`.
 * @returns {{
 *   showModal: (element: Event, type: 'privacy'|'about'|'cookies'|'terms') => Promise<void>,
 *   hideModal: () => void,
 *   modalContent: { title: string, content: string },
 *   localLegalUrls: {terms: string|null, privacy: string|null, cookies: string|null},
 *   isModalOpen: boolean,
 *   loading: boolean,
 * }}
 */
const useTermsAndPrivacyContent = ({
    path,
    lang,
    localeSettings,
    fetchLocal = false,
}) => {
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({
        title: '',
        content: '',
    });
    const contentCacheRef = useRef(new Map());

    const localLegalUrls = useMemo(
        () => getLocalLegalUrls(localeSettings[lang], lang, path),
        [localeSettings, lang, path]
    );

    useEffect(() => {
        setModalContent({ title: '', content: '' });
    }, [lang]);

    useEffect(() => {
        contentCacheRef.current.clear();
    }, [lang, localeSettings, path, fetchLocal]);

    const showModal = async (element, type) => {
        element.preventDefault();
        setIsModalOpen(true);
        setLoading(true);

        try {
            const localUrl = element.currentTarget?.href ?? element.target.href;
            const remoteUrl = localeSettings[lang]?.[type]?.link;
            const text = await getContent({
                type,
                localUrl,
                remoteUrl,
                fetchLocal,
                lang,
                cache: contentCacheRef.current,
            });

            setModalContent(text);
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            setLoading(false);
        }
    };

    const hideModal = () => {
        setIsModalOpen(false);
        setLoading(false);
        setModalContent({ title: '', content: '' });
    };

    return {
        loading,
        isModalOpen,
        modalContent,
        localLegalUrls,
        showModal,
        hideModal,
    };
};

export default useTermsAndPrivacyContent;
